import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Table } from 'antd';
import { startCase } from 'lodash';
import PropTypes from 'prop-types';
import { useState } from 'react';
import styled from 'styled-components';
import WarrantyDetailsModal from './WarrantyDetailsModal';

const CustomTable = styled(Table)`
  .ant-table-thead th {
    font-size: 13px;
  }
`;

const WarrantiesToAdd = ({
  warranties = [],
  assets = [],
  vendors = [],
  onClickDelete = () => {},
  onBulkDeleteWarranties = () => {},
  onUpdateWarranty,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [warrantyToEdit, setWarrantyToEdit] = useState(null);

  const handleSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleClickEdit = (warranty) => {
    setWarrantyToEdit({
      ...warranty,
      id: warranty.id,
      name: warranty.warrantyName,
      coverage_type: warranty.coverageType,
      id_contact: warranty.vendor,
      start_date: warranty.startDate,
      coverage_duration: warranty.coverageDuration,
      coverage_duration_unit: warranty.coverageDurationPeriod,
      end_date: warranty.endDate,
      start_meter: warranty.startMeter,
      range_meter: warranty.range,
      end_meter: warranty.endMeter,
      documents: warranty.documents,
      components: warranty.components,
    });
  };

  const handleWarrantyEdit = (data) => {
    if (onUpdateWarranty) {
      onUpdateWarranty(data);
      setWarrantyToEdit(null);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: handleSelectChange,
  };

  const columns = [
    {
      title: 'Unit IDs',
      dataIndex: 'assets',
      key: 'assets',
      render: (arr) => {
        return arr
          .map((assetId) => {
            const asset = assets.find((asset) => asset.id === assetId);
            return asset ? asset.unitId : assetId;
          })
          .join(', ');
      },
    },
    {
      title: 'Name',
      dataIndex: 'warrantyName',
      key: 'warrantyName',
    },
    {
      title: 'Coverage Type',
      dataIndex: 'coverageType',
      key: 'coverageType',
      sorter: (a, b) => a.coverageType.localeCompare(b.coverageType),
      render: (coverageType) => startCase(coverageType),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      key: 'vendor',
      render: (vendorId) => {
        const vendor = vendors.find((vendor) => vendor.id === vendorId);
        return vendor ? vendor.name : vendorId;
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => (date ? date.toDate().toLocaleDateString() : ''),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      sorter: (a, b) => new Date(a.endDate) - new Date(b.endDate),
      render: (date) => (date ? date.toDate().toLocaleDateString() : ''),
    },
    {
      title: 'Start Meter',
      dataIndex: 'startMeter',
      key: 'startMeter',
    },
    {
      title: 'Range',
      dataIndex: 'range',
      key: 'range',
    },
    {
      title: 'End Meter',
      dataIndex: 'endMeter',
      key: 'endMeter',
      sorter: (a, b) => {
        const getNumericValue = (value) => {
          if (typeof value === 'number') return value;
          if (!value) return 0;
          return parseInt(String(value).replace(/[^0-9]/g, '') || 0);
        };

        return getNumericValue(a.endMeter) - getNumericValue(b.endMeter);
      },
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <>
          <Popconfirm
            title="Are you sure you want to delete this warranty?"
            onConfirm={() => onClickDelete(record.key)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} />
          </Popconfirm>
          <Button
            onClick={() => handleClickEdit(record)}
            type="text"
            icon={<EditOutlined />}
          />
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 12, width: '100%' }}>
      <Button
        disabled={selectedRowKeys.length === 0}
        type="primary"
        style={{ marginBottom: 12 }}
        onClick={() => {
          onBulkDeleteWarranties(selectedRowKeys);
          setSelectedRowKeys([]);
        }}
      >
        Remove Warranty(s)
      </Button>

      <CustomTable
        rowKey="key"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={warranties}
      />

      <WarrantyDetailsModal
        visible={warrantyToEdit !== null}
        saving={false}
        isEditingNewWarranty={true}
        warranty={warrantyToEdit}
        onCancel={() => setWarrantyToEdit(null)}
        onSubmit={handleWarrantyEdit}
      />
    </div>
  );
};

WarrantiesToAdd.propTypes = {
  warranties: PropTypes.array,
  assets: PropTypes.array,
  vendors: PropTypes.array,
  onClickDelete: PropTypes.func,
  onBulkDeleteWarranties: PropTypes.func,
  onUpdateWarranty: PropTypes.func,
};

export default WarrantiesToAdd;
